import { ShopperAgentOutboundMessageKind } from "@redotech/crdb-enums/shopper-ai/shopper-agent-outbound-message-kind";
import * as z from "zod";
import { SupervisorDecisionSchema } from "./supervisor-review";

/** A message from either side of a Commerce Agent conversation, or a draft. */
export type ShopperAgentTraceRef = { messageId: string } | { draftId: string };

const ToolResultSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), output: z.unknown() }),
  z.object({ ok: z.literal(false), error: z.string() }),
]);

const StepSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("tool"),
    /** The call's own id, as the LLM thread holds it. */
    callId: z.string(),
    name: z.string(),
    input: z.unknown(),
    /** Null when the call produced no result, as with a handoff. */
    result: ToolResultSchema.nullable(),
  }),
  z.object({ kind: z.literal("drafted"), body: z.string() }),
]);

const ReviewSchema = z.object({
  at: z.date(),
  decision: SupervisorDecisionSchema,
  feedback: z.string(),
  /** The exact draft the supervisor read; null when its log does not hold one. */
  judged: z.string().nullable(),
  /**
   * How many of the run's tool calls had already happened when the supervisor
   * read the draft; null when the review kept no copy of what it was shown.
   */
  toolCallsSeen: z.number().nullable(),
});

const OutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("sent"),
    messageId: z.string(),
    /** What went out; null when the message row holds no text. */
    body: z.string().nullable(),
    sentAt: z.date(),
    /** The reviewer who sent it from the queue; null when no person did. */
    sentBy: z.string().nullable(),
    /** The agent's draft, when the reviewer changed it before sending. */
    editedFrom: z.string().nullable(),
  }),
  z.object({ kind: z.literal("waiting"), draftId: z.string() }),
  z.object({ kind: z.literal("not_sent") }),
]);

/** What the shopper wrote; body and time are null when the message row is gone. */
const ShopperMessageSchema = z.object({
  id: z.string(),
  body: z.string().nullable(),
  sentAt: z.date().nullable(),
});

/** One go at writing one message. */
const AttemptSchema = z.object({
  kind: z.enum(ShopperAgentOutboundMessageKind),
  /** Auto-send, or a suggestion for a person; null when the log does not say. */
  mode: z.enum(["autopilot", "copilot"]).nullable(),
  startedBy: z.union([
    z.object({ shopperMessages: z.array(ShopperMessageSchema) }),
    z.object({ triggerKey: z.string().nullable() }),
  ]),
  /** In LLM thread order; null when the steps could not be tied to this attempt. */
  steps: z.array(StepSchema).nullable(),
  /** Every supervisor verdict, oldest first. */
  reviews: z.array(ReviewSchema),
  outcome: OutcomeSchema,
  llmThreadId: z.string().nullable(),
  runId: z.string().nullable(),
});

export const ShopperAgentTraceSchema = z.object({
  /** A replaced draft never went out; its attempt is the one whose draft took its place. */
  asked: z.enum([
    "agent_message",
    "shopper_message",
    "draft",
    "replaced_draft",
  ]),
  teamId: z.string(),
  channelThreadId: z.string(),
  channel: z.string(),
  /**
   * Agent message or draft: the attempt that wrote it. Shopper message:
   * every attempt that answered it; empty when none is recorded.
   */
  attempts: z.array(AttemptSchema),
});

export type ShopperAgentTrace = z.infer<typeof ShopperAgentTraceSchema>;
export type ShopperAgentTraceAttempt = ShopperAgentTrace["attempts"][number];
export type ShopperAgentTraceStep = NonNullable<
  ShopperAgentTraceAttempt["steps"]
>[number];
export type ShopperAgentTraceReview =
  ShopperAgentTraceAttempt["reviews"][number];
export type ShopperAgentTraceShopperMessage = z.infer<
  typeof ShopperMessageSchema
>;
